import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Button from '../../../components/Button/Button'
import {bulkUploadUsers} from '../../../../api/AdminAPI'

type Props = {
  onClose: () => void
  onUploaded: () => void
}

const sampleCsv = `First Name,Last Name,Organization Email,Personal Email,Gender,DOB,Employment Type,Designation,Department,In Time,Out Time,Date of Joining,Date of Leaving,Mobile Number,Password,Reporting Manager,Role,Employee Code,Human Resource,Address
Parth,Sharma,parth@ctrine.com,parth@gmail.com,Male,15-09-1995,Employee,Tech Lead,IT,09:00,18:00,01-09-2025,,+918888888888,Temp@123,admin@ctrine.com,Employee,CTR-1234,hr@ctrine.com,"Pune, India"
`

export default function BulkUserUpload({onClose, onUploaded}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)

  const handleDownloadTemplate = () => {
    const blob = new Blob([sampleCsv], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'user-upload-template.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null
    if (f) {
      const name = f.name.toLowerCase()
      if (!(name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls'))) {
        alert('Please select a .csv or .xlsx file')
        return
      }
    }
    setResult(null) // clear previous results when picking a new file
    setFile(f)
  }

  const handleUpload = async () => {
    if (!file) return
    setLoading(true)
    try {
      const res = await bulkUploadUsers(file)
      setResult(res?.data || null)
    } catch (err) {
      // Swallow to avoid runtime error and show a generic message in result
      setResult({ totalRecords: 0, successfullyAdded: 0, failedRecords: 0, errors: [], message: 'Upload failed. Please try again.' })
    } finally {
      setLoading(false)
      // Always clear selected file name so user can re-upload immediately
      setFile(null)
      setFileInputKey((k) => k + 1)
    }
  }

  return (
    <Modal show onHide={onClose} size='lg'>
      <Modal.Header closeButton className='border-0'>
        <Modal.Title>
          <i className='fas fa-users me-2'></i>
          Upload Users (Excel/CSV)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3'>
          <div>
            <div className='fw-semibold mb-1'>Template</div>
            <div className='text-muted small'>Contains required headers and a sample row.</div>
          </div>
          <Button variant='secondary' size='sm' onClick={handleDownloadTemplate} leftIcon={<i className='fas fa-download' />}>Download Template</Button>
        </div>
        <div className='alert alert-info py-2 px-3 mb-4' style={{fontSize: '13px'}}>
          <div className='fw-semibold mb-1'>Important Instructions</div>
          <ul className='mb-0 ps-3'>
            <li>Do not edit or delete the header row or the sample row.</li>
            <li>Enter your actual user records starting from the row immediately below the sample row.</li>
            <li>Ensure all required columns are filled exactly as shown to avoid upload errors.</li>
            <li><strong>Date Format:</strong> Enter dates in DD-MM-YYYY format with hyphens (e.g., 02-09-2025). The system now automatically handles Excel date formats, so you can enter dates normally.</li>
            <li><strong>Tip:</strong> For best results with dates, format the date columns (DOB, Date of Joining, Date of Leaving) as Text in Excel before entering values to maintain your preferred format.</li>
          </ul>
        </div>

        <div className='mb-4'>
          <div className='fw-semibold mb-2'>Upload file</div>
          <input key={fileInputKey} className='form-control' type='file' accept='.csv,.xlsx,.xls' onChange={handleFileChange} />
          <div className='text-muted small mt-1'>Accepted formats: .csv, .xlsx, .xls. Max 10MB.</div>
        </div>

        <div className='d-flex justify-content-end'>
          <Button variant='primary' size='sm' onClick={handleUpload} disabled={!file || loading} leftIcon={<i className='fas fa-upload' />}>{loading ? 'Uploading...' : 'Upload'}</Button>
        </div>
        {result && (
          <div className='mt-4'>
            <div className='mb-2'>
              <strong>Total Records:</strong> {result.totalRecords} &nbsp;|
              &nbsp;<strong>Successfully Added:</strong> {result.successfullyAdded} &nbsp;|
              &nbsp;<strong>Failed Records:</strong> {result.failedRecords}
            </div>
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <div className='table-responsive'>
                <table className='table table-sm'>
                  <thead>
                    <tr>
                      <th>Row No</th>
                      <th>Name</th>
                      <th>Error Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e: any, idx: number) => (
                      <tr key={idx}>
                        <td>{e.row}</td>
                        <td>{e.name}</td>
                        <td>
                          {typeof e.error === 'string' && e.error.includes(';') ? (
                            <ul className='mb-0'>
                              {e.error.split(';').map((msg: string, i: number) => (
                                <li key={i}>{msg.trim()}</li>
                              ))}
                            </ul>
                          ) : (
                            <span>{e.error}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='border-0'>
        <Button variant='secondary' size='sm' onClick={onClose}>Close</Button>
        {result && result.failedRecords === 0 && (
          <Button variant='primary' size='sm' onClick={onUploaded}>Done</Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}


